import { ConsentTypeContext } from '@/hooks/use-consent-context/types';
import {
    DS_SHAPE_BORDER_RADIUS,
    DS_SHAPE_SPACING,
    DS_SPACING_UNITS,
} from '@calm/evexia-design-tokens';
import { CirclePartner } from '@calm/evexia-web-sdk';
import AppLogo from '@rhg/components/app-logo';
import { PartnerConfigLogo } from '@rhg/components/partner-logo';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { Box, Paper, Skeleton, Stack, Typography } from '@rhg/libs/material';
import { addLangParam } from '@rhg/utils/routes';
import { ReactNode, useId } from 'react';

const styleConsentContent = {
    border: 'none',
    height: '100%',
    width: '100%',
    borderRadius: DS_SHAPE_BORDER_RADIUS.LARGE / DS_SHAPE_SPACING.BASE,
    flexGrow: 1,
};
const stylePaper = { width: '100%' };
const styleConsentContentPaper = {
    width: '100%',
    flexGrow: 1,
    display: 'flex',
};

interface SponsorSectionProps {
    sponsorPartner?: CirclePartner;
    showCalmHealthDefault?: boolean;
    title?: string;
    subtitle?: string;
    imageOverride?: ReactNode;
}
function SponsorSection({
    sponsorPartner,
    showCalmHealthDefault,
    title,
    subtitle,
    imageOverride,
}: SponsorSectionProps) {
    const isMobile = useIsMobile();

    const showCalmHealth =
        showCalmHealthDefault || !sponsorPartner?.config?.logo;

    return (
        <Paper sx={stylePaper}>
            <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                m={DS_SPACING_UNITS.CONTENT}
            >
                <Box mb={4}>
                    {imageOverride ? (
                        imageOverride
                    ) : showCalmHealth ? (
                        <AppLogo clickable={false} />
                    ) : (
                        <PartnerConfigLogo
                            size={isMobile ? 'small' : 'large'}
                            config={sponsorPartner?.config}
                            clickable={false}
                        />
                    )}
                </Box>
                {title && (
                    <Typography
                        color="text.body"
                        variant="body1Demi"
                        component="p"
                    >
                        {title}
                    </Typography>
                )}
                {subtitle && (
                    <Typography
                        color="text.sub"
                        variant="subtitle2"
                        component="p"
                        mt={4}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
}

function ConsentContents({ src, title }: { src: string; title: string }) {
    return (
        <Paper sx={styleConsentContentPaper}>
            {src ? (
                <Box
                    title={title}
                    component="iframe"
                    src={src}
                    sx={styleConsentContent}
                />
            ) : (
                <Skeleton sx={styleConsentContent} />
            )}
        </Paper>
    );
}

interface ConsentDisplayProps {
    consentContext?: ConsentTypeContext;
    sponsorPartner?: CirclePartner;
    showCalmHealthDefault?: boolean;
    title: string;
    srTitle?: string;
    subtitle?: string;
    fallbackUrl?: string;
    imageOverride?: ReactNode;
}
export const ConsentDisplay = ({
    consentContext,
    sponsorPartner,
    title,
    srTitle,
    subtitle,
    showCalmHealthDefault,
    fallbackUrl = '',
    imageOverride,
}: ConsentDisplayProps) => {
    const id = useId();
    const currentLanguage = useCurrentLanguage();
    const baseUrl = consentContext?.link ? consentContext?.link : fallbackUrl;
    const url = baseUrl ? addLangParam(currentLanguage, baseUrl) : '';

    return (
        <>
            <Typography variant="srOnly" id={id}>
                {srTitle ?? title}
            </Typography>
            <Stack
                direction="column"
                gap={DS_SPACING_UNITS.CONTENT}
                width="100%"
                height="100%"
            >
                <SponsorSection
                    sponsorPartner={sponsorPartner}
                    showCalmHealthDefault={showCalmHealthDefault}
                    title={title}
                    subtitle={subtitle}
                    imageOverride={imageOverride}
                />

                <ConsentContents src={url} title={srTitle ?? title} />
            </Stack>
        </>
    );
};
